export const normalizeKey = (value) => String(value || '').trim().toLowerCase();

export const getStableItemId = (item, type = 'account') => {
  if (!item) return '';
  if (type === 'account') {
    return item.id || item.account_id || item.tradeline_id || '';
  }
  if (type === 'inquiry') {
    return item.id || item.inquiry_id || '';
  }
  return item.id || item.item_id || '';
};

export const getExcludedItemFingerprint = (item) => [
  normalizeKey(item.type || item.item_type || 'inquiry'),
  normalizeKey(item.bureau || item.bureau_code),
  normalizeKey(
    item.creditor_name
    || item.creditor
    || item.company_name
    || item.name
    || item.source
    || item.furnisher
  ),
  normalizeKey(item.date || item.inquiry_date || item.reported_date),
].join('|');

export const getCatalogItemFingerprint = (item, type = 'account') => {
  if (type === 'inquiry') {
    return getExcludedItemFingerprint({
      ...item,
      item_type: 'inquiry',
      creditor: item.company_name || item.creditor_name || item.inquiry_name || item.creditor,
      inquiry_date: item.inquiry_date || item.date,
    });
  }
  if (type === 'personal_info') {
    return [
      'personal_info',
      normalizeKey(item.field || item.fieldLabel || item.field_label),
      normalizeKey(getStableItemId(item, 'personal_info')),
    ].join('|');
  }
  return [
    'account',
    normalizeKey(item.bureau),
    normalizeKey(item.creditor_name || item.account_name),
    normalizeKey(item.account_number || item.account_number_masked),
  ].join('|');
};

export const formatExcludedDisplayLabel = (item) => {
  if (item?.display_label) return item.display_label;

  const creditor = item?.creditor
    || item?.creditor_name
    || item?.company_name
    || item?.inquiry_name
    || item?.name
    || 'Unknown';
  const date = item?.inquiry_date || item?.date || item?.reported_date || '';
  const bureau = item?.bureau || item?.bureau_code || '';

  if (date && bureau) return `${creditor} · ${bureau} · ${date}`;
  if (date) return `${creditor} — ${date}`;
  return String(creditor);
};

export const normalizeExcludedItems = (excludedItems = []) => (
  (excludedItems || []).map((item) => {
    if (!item) return null;
    if (typeof item === 'string') {
      return {
        item_id: item,
        item_type: 'inquiry',
        reason: 'Item excluded by admin rules',
        display_label: item,
        fingerprint: '',
      };
    }

    const normalized = {
      item_id: item.item_id || item.id || '',
      item_type: item.item_type || item.type || 'inquiry',
      reason: item.reason || 'Item excluded by admin rules',
      display_label: formatExcludedDisplayLabel(item),
      inquiry_date: item.inquiry_date || item.date || '',
      bureau: item.bureau || item.bureau_code || '',
      creditor: item.creditor || item.creditor_name || item.company_name || '',
      status: item.status || 'excluded',
    };
    normalized.fingerprint = getExcludedItemFingerprint(normalized);
    return normalized;
  }).filter(Boolean)
);

export const buildExcludedMatcher = (normalizedExcludedItems = []) => {
  const ids = new Set();
  const fingerprints = new Set();

  normalizedExcludedItems.forEach((item) => {
    if (item.item_id) ids.add(item.item_id);
    if (item.fingerprint) fingerprints.add(item.fingerprint);
  });

  const isExcluded = (item, type = 'inquiry') => {
    if (!item) return false;

    const id = getStableItemId(item, type);
    if (id && ids.has(id)) return true;

    const catalogFingerprint = getCatalogItemFingerprint(item, type);
    if (fingerprints.has(catalogFingerprint)) return true;

    const inquiryFingerprint = getExcludedItemFingerprint({
      ...item,
      item_type: type === 'personal_info' ? 'personal_info' : type,
      creditor: item.creditor || item.creditor_name || item.company_name || item.inquiry_name,
      inquiry_date: item.inquiry_date || item.date,
    });
    return fingerprints.has(inquiryFingerprint);
  };

  return { ids, fingerprints, isExcluded };
};

export const isEligibleForDefaultSelection = (
  item,
  type,
  { excludedIds = new Set(), protectedPIIds = [], excludedMatcher = null } = {}
) => {
  if (!item) return false;

  const id = getStableItemId(item, type);
  if (!id) return false;
  if (item.excluded === true) return false;
  if (item.protected === true) return false;
  if (item.isEligible === false) return false;
  if (item.proceeding === false) return false;
  if (excludedIds.has(id)) return false;
  if (excludedMatcher?.isExcluded?.(item, type)) return false;
  if (type === 'personal_info' && protectedPIIds.includes(id)) return false;

  return true;
};

export const buildEligibleDisplayedItemsKey = ({
  selectedReportId = '',
  eligibleAccounts = [],
  eligibleInquiries = [],
  eligiblePersonalInfo = [],
} = {}) => {
  const parts = [
    selectedReportId,
    ...eligibleAccounts.map((item) => getStableItemId(item, 'account')),
    ...eligibleInquiries.map((item) => getStableItemId(item, 'inquiry')),
    ...eligiblePersonalInfo.map((item) => getStableItemId(item, 'personal_info')),
  ];
  return parts.filter(Boolean).sort().join('|');
};

export const buildCatalogDisplayedItemsKey = ({
  selectedReportId = '',
  accounts = [],
  inquiries = [],
  personalInfo = [],
} = {}) => buildEligibleDisplayedItemsKey({
  selectedReportId,
  eligibleAccounts: accounts,
  eligibleInquiries: inquiries,
  eligiblePersonalInfo: personalInfo,
});

export const splitDisplayedCatalogItems = ({
  accounts = [],
  inquiries = [],
  personalInfo = [],
  excludedMatcher = null,
  protectedPIIds = [],
}) => {
  const eligibleAccounts = accounts.filter((item) => isEligibleForDefaultSelection(item, 'account', { excludedMatcher }));
  const excludedAccounts = accounts.filter((item) => excludedMatcher?.isExcluded?.(item, 'account'));

  const eligibleInquiries = inquiries.filter((item) => isEligibleForDefaultSelection(item, 'inquiry', { excludedMatcher }));
  const excludedInquiries = inquiries.filter((item) => excludedMatcher?.isExcluded?.(item, 'inquiry'));

  const eligiblePersonalInfo = personalInfo.filter((item) => isEligibleForDefaultSelection(item, 'personal_info', {
    excludedMatcher,
    protectedPIIds,
  }));
  const excludedPersonalInfo = personalInfo.filter((item) => excludedMatcher?.isExcluded?.(item, 'personal_info'));

  return {
    eligibleAccounts,
    eligibleInquiries,
    eligiblePersonalInfo,
    excludedAccounts,
    excludedInquiries,
    excludedPersonalInfo,
  };
};

export const buildExcludedDisplayedItems = ({
  normalizedExcludedItems = [],
  accounts = [],
  inquiries = [],
  personalInfo = [],
  excludedMatcher = null,
}) => {
  const seen = new Set();
  const displayed = [];

  const pushItem = (item) => {
    const key = item.fingerprint || item.item_id || item.display_label;
    if (!key || seen.has(key)) return;
    seen.add(key);
    displayed.push(item);
  };

  normalizedExcludedItems.forEach((item) => pushItem(item));

  const catalogBuckets = [
    [accounts, 'account'],
    [inquiries, 'inquiry'],
    [personalInfo, 'personal_info'],
  ];

  catalogBuckets.forEach(([items, type]) => {
    (items || []).forEach((catalogItem) => {
      if (!excludedMatcher?.isExcluded?.(catalogItem, type)) return;
      const itemId = getStableItemId(catalogItem, type);
      if (normalizedExcludedItems.some((excluded) => excluded.item_id === itemId)) return;

      pushItem({
        item_id: itemId,
        item_type: type,
        reason: 'Item excluded by admin rules',
        display_label: formatExcludedDisplayLabel(catalogItem),
        inquiry_date: catalogItem.inquiry_date || catalogItem.date || '',
        bureau: catalogItem.bureau || catalogItem.bureau_code || '',
        creditor: catalogItem.creditor_name || catalogItem.company_name || catalogItem.creditor || '',
        fingerprint: getCatalogItemFingerprint(catalogItem, type),
        status: 'excluded',
      });
    });
  });

  return displayed;
};

export const pruneSelectedItemsForExclusions = (
  selectedItems,
  {
    accounts = [],
    inquiries = [],
    personalInfo = [],
    excludedMatcher = null,
  } = {}
) => {
  const filterIds = (ids, catalogItems, type) => (
    (ids || []).filter((id) => {
      if (excludedMatcher?.ids?.has(id)) return false;
      const item = catalogItems.find((row) => getStableItemId(row, type) === id);
      if (!item) return false;
      return !excludedMatcher?.isExcluded?.(item, type);
    })
  );

  return {
    accounts: filterIds(selectedItems.accounts, accounts, 'account'),
    inquiries: filterIds(selectedItems.inquiries, inquiries, 'inquiry'),
    personalInfo: filterIds(selectedItems.personalInfo, personalInfo, 'personal_info'),
  };
};

export const buildSelectAllEligibleSelection = ({
  eligibleAccounts = [],
  eligibleInquiries = [],
  eligiblePersonalInfo = [],
} = {}) => ({
  accounts: eligibleAccounts.map((item) => item.id),
  inquiries: eligibleInquiries.map((item) => item.id),
  personalInfo: eligiblePersonalInfo.map((item) => item.id),
});

const normalizeForId = (value) => normalizeKey(value).replace(/\s+/g, ' ').trim();

const hashString = (value) => {
  const input = String(value || '');
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = ((hash << 5) - hash) + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
};

export const buildDeterministicFallbackItemId = (item, type = 'unknown') => {
  const normalizedType = normalizeForId(type || item?.item_type || item?.type || 'unknown');
  const normalizedBureau = normalizeForId(item?.bureau || item?.bureau_code || (item?.bureaus || [])[0] || 'unknown');
  const normalizedCreditor = normalizeForId(
    item?.creditor_name
    || item?.creditor
    || item?.company_name
    || item?.inquiry_name
    || item?.account_name
    || item?.name
    || ''
  );
  const normalizedAccountNumber = normalizeForId(
    item?.account_number_masked
    || item?.account_number
    || item?.accountNumber
    || item?.last4
    || ''
  );
  const normalizedDate = normalizeForId(
    item?.inquiry_date
    || item?.date
    || item?.date_reported
    || item?.reported_date
    || ''
  );
  const normalizedPiValue = normalizeForId(
    item?.normalized_value
    || item?.display_value
    || item?.disputed_value
    || item?.correct_value
    || ''
  );
  const raw = [
    normalizedType,
    normalizedBureau,
    normalizedCreditor,
    normalizedAccountNumber,
    normalizedDate,
    normalizedPiValue,
  ].join('|');
  return `${normalizedType || 'item'}_${hashString(raw)}`;
};

export const buildDisputeWizardNormalizedState = ({
  allItems = [],
  selectedItemIds = [],
  excludedItemIds = [],
} = {}) => {
  const selectedSet = new Set((selectedItemIds || []).map((id) => String(id)));
  const excludedSet = new Set((excludedItemIds || []).map((id) => String(id)));
  const all = allItems || [];

  const proceedingItemIds = [...selectedSet].filter((id) => !excludedSet.has(id));
  const proceedingSet = new Set(proceedingItemIds);
  const visibleItems = all.filter((item) => !excludedSet.has(String(item?.id || '')));
  const excludedItems = all.filter((item) => excludedSet.has(String(item?.id || '')));
  const proceedingItems = all.filter((item) => proceedingSet.has(String(item?.id || '')));

  return {
    selectedSet,
    excludedSet,
    proceedingSet,
    proceedingItemIds,
    visibleItems,
    excludedItems,
    proceedingItems,
  };
};
